import StoryText from "@/app/(site)/_components/Atoms/Storytext"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { getGeneralLinkHref } from "@/src/utils/links/getGeneralLinkHref"
import { type GeneralLink } from "@/types"
import { PortableText } from "@portabletext/react"
import { type ReactNode } from "react"
import type { PortableTextBlock } from "sanity"

interface Renderers {
    block?: {
        normal?: (children: ReactNode) => ReactNode
        preamble?: (children: ReactNode) => ReactNode
        "18"?: (children: ReactNode) => ReactNode
        "16"?: (children: ReactNode) => ReactNode
        "14"?: (children: ReactNode) => ReactNode
        h2?: (children: ReactNode) => ReactNode
        h3?: (children: ReactNode) => ReactNode
        h4?: (children: ReactNode) => ReactNode
        quote?: (children: ReactNode) => ReactNode
    }
    list?: {
        bullet?: (children: ReactNode) => ReactNode
        number?: (children: ReactNode) => ReactNode
    }
}

type Props = {
    content: PortableTextBlock[]
    render?: Renderers
    paragraphStyling?: string
}

const PortableTextRenderer: React.FC<Props> = ({ content, render }) => {
    const components = {
        types: {
            lineBreakElement: () => <br />
        },
        block: {
            normal: ({ children }: { children?: React.ReactNode }) => {
                if (render?.block?.normal) {
                    return render.block.normal(children)
                }

                return (
                    <StoryText size="size3" className="mb-24 last:mb-0">
                        {children}
                    </StoryText>
                )
            },
            "14": ({ children }: { children?: React.ReactNode }) => {
                if (render?.block?.["14"]) {
                    return render.block["14"](children)
                }
            },
            "16": ({ children }: { children?: React.ReactNode }) => {
                if (render?.block?.["16"]) {
                    return render.block["16"](children)
                }
            },
            "18": ({ children }: { children?: React.ReactNode }) => {
                if (render?.block?.["18"]) {
                    return render.block["18"](children)
                }
            },
            h2: ({ children }: { children?: React.ReactNode }) => {
                if (render?.block?.["h2"]) {
                    return render.block["h2"](children)
                }
            },
            h3: ({ children }: { children?: React.ReactNode }) => {
                if (render?.block?.["h3"]) {
                    return render.block["h3"](children)
                }
            },
            h4: ({ children }: { children?: React.ReactNode }) => {
                if (render?.block?.["h4"]) {
                    return render.block["h4"](children)
                }
            },
            quote: ({ children }: { children?: React.ReactNode }) => {
                if (render?.block?.["quote"]) {
                    return render.block["quote"](children)
                }
            },
            preamble: ({ children }: { children?: React.ReactNode }) => {
                if (render?.block?.preamble) {
                    return render.block.preamble(children)
                }
                return (
                    <StoryText size="size2" className="mb-24 last:mb-0">
                        {children}
                    </StoryText>
                )
            }
        },
        marks: {
            strong: ({ children }: { children?: React.ReactNode }) => (
                <span className="font-tradeBoldNew">{children}</span>
            ),
            link: ({
                children,
                value
            }: {
                children?: React.ReactNode
                value?: GeneralLink
            }) => {
                const href = value ? getGeneralLinkHref(value) : undefined
                if (typeof href === "undefined") return children

                return value?.external ? (
                    <a
                        href={href}
                        target="_blank"
                        rel={`noopener noreferrer ${href.includes("https://returns.poc.com/customer") ? "nofollow" : ""}`} // special treatment for returns.poc.com
                        className="underline hover:no-underline"
                    >
                        {children}
                    </a>
                ) : (
                    <DynamicLink href={href} className="underline hover:no-underline">
                        {children}
                    </DynamicLink>
                )
            },
            generalLink: ({
                children,
                value
            }: {
                children?: React.ReactNode
                value?: GeneralLink
            }) => {
                const href = value ? getGeneralLinkHref(value) : undefined
                if (typeof href === "undefined") return children

                return value?.external ? (
                    <a
                        className="underline hover:no-underline"
                        href={href}
                        target="_blank"
                        rel={`noopener noreferrer ${href.includes("https://returns.poc.com/customer") ? "nofollow" : ""}`} // special treatment for returns.poc.com
                    >
                        {children}
                    </a>
                ) : (
                    <DynamicLink href={href} className="underline hover:no-underline">
                        {children}
                    </DynamicLink>
                )
            }
        },
        list: {
            bullet: ({ children }: { children?: React.ReactNode }) => {
                if (render?.list?.bullet) {
                    return render.list.bullet(children)
                }

                return (
                    <StoryText asChild size="size3" className="mb-24 last:mb-0">
                        <ul className="list-disc space-y-2 pl-[1em]">{children}</ul>
                    </StoryText>
                )
            },
            number: ({ children }: { children?: React.ReactNode }) => {
                if (render?.list?.number) {
                    return render.list.number(children)
                }

                return (
                    <StoryText asChild size="size3" className="mb-24 last:mb-0">
                        <ol className="list-decimal space-y-2 pl-[1em]">{children}</ol>
                    </StoryText>
                )
            }
        },
        listItem: {
            bullet: ({ children }: { children?: React.ReactNode }) => (
                <li className="ml-4">{children}</li>
            ),
            number: ({ children }: { children?: React.ReactNode }) => (
                <li className="ml-4">{children}</li>
            )
        }
    }

    return <PortableText value={content} components={components} />
}

export default PortableTextRenderer
